// src/hooks/useQcDelayGate.tsx
//
// EE-216 - the gate in front of closing the last QC step of a production line
// that is already past its Production Deadline.
//
// It lives in a hook rather than in the QC page because a QC task can be
// closed from three places (the QC dashboard, Project Management's ProjectRow,
// and CustomTaskButton) and all three should behave the same. The real
// guarantee is still server-side: ProductionController.update_production_task
// returns 422 DELAY_REASON_REQUIRED whatever the caller is. This is the UX
// half - ask before the request rather than after it fails.
import { ReactElement, useState } from "react";
import dayjs from "dayjs";
import QcDelayReasonDialog, {
  QcDelaySubmission,
} from "components/Modals/QcDelayReasonDialog";
import {
  ProductionTaskStateEnum,
  ProductionTaskType,
  ProductionVMType,
  UpdateProductionTaskType,
} from "types/ProductionType";

// A QC step carries no flag or enum - it is a plain description match, set
// when the backend generates the task templates (__prep_product_tasks).
// The task's `name` is "afterwork" for QC, but a CNC production can get a
// SECOND "QC" task appended after custom-finish tasks, and only the
// description distinguishes them, so this is the marker that can answer
// "which is the last one".
const QC_TASK_DESCRIPTION = "QC";

const isQcTask = (task: ProductionTaskType) =>
  (task.description ?? "").trim().toUpperCase() === QC_TASK_DESCRIPTION;

export const findLastQcTask = (
  tasks: ProductionTaskType[]
): ProductionTaskType | undefined => {
  const qcTasks = tasks.filter(isQcTask);
  if (qcTasks.length === 0) {
    return undefined;
  }
  // Highest priority wins; id breaks a tie so the choice is stable.
  return qcTasks.reduce((last, task) =>
    (task.priority ?? 0) > (last.priority ?? 0) ||
    ((task.priority ?? 0) === (last.priority ?? 0) && task.id > last.id)
      ? task
      : last
  );
};

type PropsType = {
  project: ProductionVMType;
  /**
   * Performs the actual update. Called straight through when the gate does not
   * apply, and with the delay fields merged in once the dialog is confirmed.
   */
  onProceed: (
    changes: Partial<ProductionTaskType>,
    delay?: QcDelaySubmission
  ) => void | Promise<unknown>;
};

type GateType = {
  /**
   * Returns true when the change was intercepted and the dialog is now open -
   * the caller must not perform its own update in that case.
   */
  guard: (changes: Partial<ProductionTaskType>) => boolean;
  dialog: ReactElement;
  /**
   * Bump this into the state button's `key`. Cancelling the dialog leaves the
   * task untouched, so nothing the button observes changes and its optimistic
   * local state would keep showing DONE - remounting re-seeds it.
   */
  resetNonce: number;
};

const useQcDelayGate = ({ project, onProceed }: PropsType): GateType => {
  const [pending, setPending] = useState<Partial<ProductionTaskType>>();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetNonce, setResetNonce] = useState(0);

  const guard = (changes: Partial<ProductionTaskType>) => {
    if (changes.state !== ProductionTaskStateEnum.DONE) {
      return false;
    }
    const task = project.tasks.find((t) => t.id === changes.id);
    if (!task || task.state === ProductionTaskStateEnum.DONE) {
      // Already closed - re-saving an unrelated field must not re-prompt.
      return false;
    }
    const lastQc = findLastQcTask(project.tasks);
    if (!lastQc || lastQc.id !== task.id) {
      return false;
    }
    // No deadline means we cannot tell whether this is late, so do not block.
    if (!project.deadlineDate) {
      return false;
    }
    if (!dayjs().isAfter(dayjs(project.deadlineDate))) {
      return false;
    }
    setPending(changes);
    return true;
  };

  const handleClose = () => {
    setPending(undefined);
    setIsSubmitting(false);
    setResetNonce((n) => n + 1);
  };

  const handleConfirm = (submission: QcDelaySubmission) => {
    if (!pending) {
      return;
    }
    setIsSubmitting(true);
    Promise.resolve(onProceed(pending, submission))
      .then(() => {
        setPending(undefined);
      })
      .catch(() => {
        // The caller toasts the failure. Bump the nonce so the button goes
        // back to what the task still is.
        setPending(undefined);
        setResetNonce((n) => n + 1);
      })
      .finally(() => setIsSubmitting(false));
  };

  return {
    guard,
    resetNonce,
    dialog: (
      <QcDelayReasonDialog
        open={!!pending}
        deadline={project.deadlineDate}
        partId={project.productId}
        isSubmitting={isSubmitting}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    ),
  };
};

/** Merges the dialog's answer into an update payload. */
export const withDelay = (
  command: UpdateProductionTaskType,
  delay?: QcDelaySubmission
): UpdateProductionTaskType =>
  delay
    ? {
        ...command,
        delayPrimaryReason: delay.delayPrimaryReason,
        delayContributingReasons: delay.delayContributingReasons,
        delayComment: delay.delayComment,
      }
    : command;

export default useQcDelayGate;
